// Native pickers do not consistently emit `change` when returning to a page.
// Keep one observer per mounted input and consume each selection only once.
export function watchFileReturn(input: HTMLInputElement, page: Document, host: Window,
  receive: (files: File[]) => void, missing: () => void) {
  let waiting = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let previous: File | undefined;
  function scan() {
    const files = Array.from(input.files ?? []);
    if (!files.length || files[0] === previous) return false;
    previous = files[0]; waiting = false; clearTimeout(timer);
    receive(files);
    return true;
  }
  function cancel() {
    if (scan()) return;
    waiting = false; clearTimeout(timer); missing();
  }
  function resume() {
    if (page.visibilityState === 'hidden') return;
    if (scan() || !waiting) return;
    clearTimeout(timer);
    let attempts = 0;
    const check = () => {
      if (page.visibilityState === 'hidden' || scan() || !waiting) return;
      if (++attempts < 40) timer = setTimeout(check, 250);
      else cancel();
    };
    check();
  }
  input.addEventListener('change', scan);
  input.addEventListener('input', scan);
  input.addEventListener('cancel', cancel);
  host.addEventListener('focus', resume);
  host.addEventListener('pageshow', resume);
  page.addEventListener('visibilitychange', resume);
  scan();
  return {
    open() { previous = undefined; input.value = ''; waiting = true; try { input.click(); } catch { cancel(); } },
    dispose() {
      clearTimeout(timer);
      input.removeEventListener('change', scan); input.removeEventListener('input', scan);
      input.removeEventListener('cancel', cancel); host.removeEventListener('focus', resume);
      host.removeEventListener('pageshow', resume); page.removeEventListener('visibilitychange', resume);
    },
  };
}
